/**
 * Create a site user — its own route, because it's the one flow with no
 * existing account to act on. On success it hands you straight to that user's
 * console instead of leaving you on an empty form.
 *
 * Validation shows on submit rather than disabling the button: a greyed-out
 * button that never says why is the thing support complained about.
 */
import { useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { ArrowLeft, Loader2 } from "lucide-react";
import {
  ASSIGN_REASONS,
  PRODUCT_PLANS,
} from "../../../../../shared/index";
import { api } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { Card } from "./parts";
import { describeError } from "./format";

const OTHER = "other";

/** Every user we create through this console originates on www. */
const ORIGIN_WWW = 1;

/** The free plan: nothing to expire, so no duration is asked for. */
const BASIC_PLAN = 1;

const DURATION_UNITS = ["day", "month", "year"] as const;
type DurationUnit = (typeof DURATION_UNITS)[number];

const selectClass =
  "h-9 w-full rounded-md border border-input bg-background px-3 text-sm";

/** `count` units from today, as the `YYYY-MM-DD` the upstream API expects. */
function expiryFromDuration(count: number, unit: DurationUnit): string {
  const d = new Date();
  if (unit === "day") d.setDate(d.getDate() + count);
  if (unit === "month") d.setMonth(d.getMonth() + count);
  if (unit === "year") d.setFullYear(d.getFullYear() + count);
  return d.toISOString().slice(0, 10);
}

type FieldErrors = Partial<
  Record<"email" | "password" | "reason" | "duration", string>
>;

export default function NewSiteUserPage() {
  const navigate = useNavigate();
  // Arriving from a failed lookup — the address is already known, don't make
  // them type it again.
  const [params] = useSearchParams();
  const [email, setEmail] = useState(params.get("email") ?? "");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [password, setPassword] = useState("");
  const [plan, setPlan] = useState<number>(PRODUCT_PLANS[0].value);
  const [durationCount, setDurationCount] = useState("1");
  const [durationUnit, setDurationUnit] = useState<DurationUnit>("year");
  const [reason, setReason] = useState("");
  const [freeReason, setFreeReason] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const isBasic = plan === BASIC_PLAN;
  const resolvedReason = reason === OTHER ? freeReason.trim() : reason;
  const count = Number(durationCount);
  const countValid =
    durationCount.trim() === "" ||
    (Number.isInteger(count) && count >= 1 && count <= 99);

  // Empty duration means one year — stated under the field, not hidden.
  const effectiveExpiry = expiryFromDuration(
    durationCount.trim() === "" || !countValid ? 1 : count,
    durationCount.trim() === "" ? "year" : durationUnit
  );

  const errors: FieldErrors = {};
  if (!email.trim()) errors.email = "An email is required";
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()))
    errors.email = "That is not a valid email address";
  if (password.length < 6) errors.password = "At least 6 characters";
  if (!resolvedReason) errors.reason = "Pick a reason, or describe one";
  if (!countValid) errors.duration = "A whole number between 1 and 99";

  const show = (field: keyof FieldErrors) =>
    submitted ? errors[field] : undefined;

  const create = useMutation({
    mutationFn: () =>
      api.siteUsers.create({
        email: email.trim().toLowerCase(),
        firstName,
        lastName,
        password,
        productPlan: plan,
        originSubDomain: ORIGIN_WWW,
        expiryDate: effectiveExpiry,
        reason: resolvedReason,
      }),
    onSuccess: (r) => {
      toast.success(r.message);
      navigate(
        `/dashboard/users?email=${encodeURIComponent(email.trim().toLowerCase())}`
      );
    },
    onError: (e) => toast.error(describeError(e)),
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    if (Object.keys(errors).length === 0) create.mutate();
    else toast.error("Some fields still need attention");
  };

  const errorClass = (field: keyof FieldErrors) =>
    show(field) && "border-destructive focus-visible:ring-destructive";

  return (
    <div className="space-y-6">
      <div>
        <Button variant="ghost" size="sm" asChild className="mb-2 -ml-2">
          <Link to="/dashboard/users">
            <ArrowLeft className="mr-2 h-4 w-4" />
            User Management
          </Link>
        </Button>
        <h1 className="text-2xl font-bold">Create user</h1>
        <p className="text-sm text-muted-foreground">
          Creates a TipRanks account with a plan already assigned.
        </p>
      </div>

      <form onSubmit={submit} noValidate className="max-w-2xl">
        <Card>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              className="sm:col-span-2"
              id="email"
              label="Email"
              error={show("email")}
            >
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="user@example.com"
                className={cn(errorClass("email"))}
                autoFocus
              />
            </Field>

            <Field id="firstName" label="First name" optional>
              <Input
                id="firstName"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
            </Field>
            <Field id="lastName" label="Last name" optional>
              <Input
                id="lastName"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
            </Field>

            <Field
              className="sm:col-span-2"
              id="password"
              label="Password"
              error={show("password")}
              hint="Shared with the user; at least 6 characters"
            >
              <Input
                id="password"
                type="text"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={cn(errorClass("password"))}
              />
            </Field>

            <Field id="plan" label="Plan">
              <select
                id="plan"
                className={selectClass}
                value={plan}
                onChange={(e) => setPlan(Number(e.target.value))}
              >
                {PRODUCT_PLANS.map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
              </select>
            </Field>

            {!isBasic && (
              <Field
                id="durationCount"
                label="Subscription length"
                error={show("duration")}
                hint={
                  durationCount.trim() === ""
                    ? `Leave empty for 1 year — expires ${effectiveExpiry}`
                    : `Expires ${effectiveExpiry}`
                }
              >
                <div className="flex gap-2">
                  <Input
                    id="durationCount"
                    type="number"
                    min={1}
                    max={99}
                    value={durationCount}
                    onChange={(e) => setDurationCount(e.target.value)}
                    className={cn("w-20", errorClass("duration"))}
                  />
                  <select
                    className={selectClass}
                    value={durationUnit}
                    onChange={(e) =>
                      setDurationUnit(e.target.value as DurationUnit)
                    }
                    aria-label="Duration unit"
                  >
                    {DURATION_UNITS.map((u) => (
                      <option key={u} value={u}>
                        {u}
                        {Number(durationCount) === 1 ? "" : "s"}
                      </option>
                    ))}
                  </select>
                </div>
              </Field>
            )}

            <Field
              className={isBasic ? "sm:col-span-2" : undefined}
              id="reason"
              label="Reason"
              error={show("reason")}
            >
              <select
                id="reason"
                className={cn(selectClass, errorClass("reason"))}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              >
                <option value="">Select a reason…</option>
                {ASSIGN_REASONS.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
                <option value={OTHER}>Other (free text)</option>
              </select>
            </Field>

            {reason === OTHER && (
              <Field
                className="sm:col-span-2"
                id="freeReason"
                label="Reason detail"
                error={show("reason")}
              >
                <Input
                  id="freeReason"
                  value={freeReason}
                  onChange={(e) => setFreeReason(e.target.value)}
                  className={cn(errorClass("reason"))}
                />
              </Field>
            )}
          </div>

          <div className="mt-6 flex items-center justify-end gap-2">
            <Button variant="outline" asChild>
              <Link to="/dashboard/users">Cancel</Link>
            </Button>
            <Button type="submit" disabled={create.isPending}>
              {create.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Create user
            </Button>
          </div>
        </Card>
      </form>
    </div>
  );
}

function Field({
  id,
  label,
  error,
  hint,
  optional,
  className,
  children,
}: {
  id: string;
  label: string;
  error?: string;
  hint?: string;
  optional?: boolean;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={cn("space-y-2", className)}>
      <Label htmlFor={id}>
        {label}
        {optional && (
          <span className="ml-1 text-xs font-normal text-muted-foreground">
            optional
          </span>
        )}
      </Label>
      {children}
      {error ? (
        <p className="text-xs font-medium text-destructive">{error}</p>
      ) : hint ? (
        <p className="text-xs text-muted-foreground">{hint}</p>
      ) : null}
    </div>
  );
}
